import { useState } from "react";
import { X, Send, Loader2, CheckCircle2, Mail, Car } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const DURATION_KEYS = ["1-3", "3-6", "6-12", "12+"];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable inquiry modal pre-filled with a specific vehicle name.
export const InquiryModal = ({ vehicleName, open, onClose }) => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", duration: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const reset = () => {
    setForm({ name: "", email: "", phone: "", duration: "", message: "" });
    setSuccess(false);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          vehicleType: vehicleName,
          duration: form.duration,
          message: form.message
            ? `Zájem o vůz: ${vehicleName}. ${form.message}`
            : `Zájem o vůz: ${vehicleName}.`,
        }),
      });
      if (!res.ok) throw new Error("fail");
      setSuccess(true);
    } catch (err) {
      setError(t("modal.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in-up"
      onClick={handleClose}
      data-testid="inquiry-modal"
    >
      <div
        className="bg-[#121212] border border-white/10 rounded-2xl p-6 sm:p-8 max-w-md w-full max-h-[90vh] overflow-y-auto no-scrollbar relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
          aria-label="Close"
          data-testid="inquiry-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {success ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-['Cabinet_Grotesk'] text-2xl font-bold mb-2">{t("modal.successTitle")}</h3>
            <p className="text-zinc-400 mb-6 text-sm">
              {t("modal.successPre")} <span className="text-white font-medium">{vehicleName}</span>. {t("modal.successPost")}
            </p>
            <button onClick={handleClose} className="btn-accent w-full" data-testid="inquiry-success-close">
              {t("modal.ok")}
            </button>
          </div>
        ) : (
          <>
            <h3 className="font-['Cabinet_Grotesk'] text-xl sm:text-2xl font-bold mb-1">{t("detail.inquiryBtn")}</h3>
            <div className="inline-flex items-center gap-1.5 text-orange-400 text-sm font-medium mb-5">
              <Car className="w-4 h-4" /> {vehicleName}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="inquiry-form">
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">{t("modal.name")}</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none transition-colors"
                  data-testid="inquiry-input-name"
                />
              </div>

              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">{t("modal.email")}</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none transition-colors"
                  data-testid="inquiry-input-email"
                />
              </div>

              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">{t("modal.phone")}</label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none transition-colors"
                  data-testid="inquiry-input-phone"
                />
              </div>

              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">{t("modal.duration")}</label>
                <select
                  value={form.duration}
                  onChange={(e) => setForm({ ...form, duration: e.target.value })}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none transition-colors"
                  data-testid="inquiry-input-duration"
                >
                  <option value="" className="bg-[#1a1a1a] text-white">{t("modal.durationPlaceholder")}</option>
                  {DURATION_KEYS.map((d) => (
                    <option key={d} value={d} className="bg-[#1a1a1a] text-white">{t(`dur.${d}`)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">{t("modal.message")}</label>
                <textarea
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder={t("modal.messagePlaceholder")}
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:border-orange-500 focus:outline-none transition-colors resize-none"
                  data-testid="inquiry-input-message"
                />
              </div>

              {error && <p className="text-red-400 text-sm" data-testid="inquiry-error">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="inquiry-submit"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t("modal.submitting")}</>
                ) : (
                  <><Send className="w-4 h-4" /> {t("modal.submit")}</>
                )}
              </button>

              <p className="flex items-center justify-center gap-1.5 text-zinc-500 text-xs">
                <Mail className="w-3.5 h-3.5" /> {t("modal.note")}
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default InquiryModal;
